import React, { useEffect, useRef, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { CheckCircleOutline, WarningAmber } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ItemAdvisory } from "./advisories";

export interface Props {
  /** Flagged dimensions for ONE text answer, strongest first. */
  advisories: ItemAdvisory[];
  /** Positive recognition for THIS answer: a model check ran over it and
   *  came back clean. Rendered in the same slot as the warnings so praise
   *  and coaching live in one consistent place; warnings always win. */
  affirm?: boolean;
}

// Entrance treatment shared by both variants: a short slide-down + fade so
// the box visibly arrives instead of popping, suppressed for reduced-motion.
const entranceSx = {
  "@keyframes coachingSlideIn": {
    from: { opacity: 0, transform: "translateY(-6px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: "coachingSlideIn 0.22s ease-out",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
  },
};

/** On first appearance, bring the box into the viewport when it mounted
 *  below the fold. block:"nearest" is a no-op when already fully visible,
 *  so an on-screen hint never yanks the page around. */
function useRevealOnMount() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, []);
  return ref;
}

/**
 * Inline coaching hint rendered directly under a written answer (FL-333),
 * in the idiom of a validation helper — but advisory-only: it never blocks
 * typing, completion, or submission. Shows the single weakest dimension
 * (highest-confidence flag) as the primary line; further dimensions sit
 * behind a "+N more" expander so one weak answer doesn't read as a wall
 * of criticism.
 */
export const InlineCoaching = ({ advisories, affirm }: Props) => {
  const [t] = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const rootRef = useRevealOnMount();

  if (advisories.length === 0) {
    if (!affirm) {
      return null;
    }
    return (
      <Box
        ref={rootRef}
        role="status"
        sx={{
          mt: 0.75,
          ml: 1.75,
          mr: 1.75,
          display: "flex",
          alignItems: "flex-start",
          gap: 0.75,
          ...entranceSx,
        }}
      >
        <CheckCircleOutline
          sx={{ fontSize: 16, color: "success.main", mt: "1px" }}
        />
        <Typography variant="caption" sx={{ color: "success.main" }}>
          {t("responseCoaching.allClear")}
        </Typography>
      </Box>
    );
  }
  const [primary, ...rest] = advisories;

  return (
    <Box
      ref={rootRef}
      role="status"
      sx={{ mt: 0.75, ml: 1.75, mr: 1.75, ...entranceSx }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        <WarningAmber sx={{ fontSize: 16, color: "warning.main", mt: "1px" }} />
        <Typography variant="caption" sx={{ color: "warning.main" }}>
          {t(primary.messageKey)}
          {rest.length > 0 && (
            <>
              {" "}
              <Link
                component="button"
                type="button"
                variant="caption"
                color="inherit"
                sx={{ fontWeight: 600, verticalAlign: "baseline" }}
                onClick={() => setExpanded((prev) => !prev)}
              >
                {expanded
                  ? t("responseCoaching.showLess")
                  : t("responseCoaching.showMore", { count: rest.length })}
              </Link>
            </>
          )}
        </Typography>
      </Box>
      {expanded &&
        rest.map((advisory) => (
          <Typography
            key={advisory.ruleId}
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ ml: 2.75, mt: 0.25 }}
          >
            {t(advisory.messageKey)}
          </Typography>
        ))}
    </Box>
  );
};
